import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Fingerprint, Loader2, Download, Link2, MessageCircle } from "lucide-react";
import SectionHeader from "./SectionHeader";
import Crest from "./Crest";
import { NATIONS, TIERS } from "../data/nations";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const shareText = (m) =>
  `I just signed the Charter of the United States of Africa — Movement ID ${m.movement_id}. One passport, one people, one destiny. Africa Without Border. ${API}/badges/${m.movement_id}.png`;
const tierLabel = (v) => TIERS.find((t) => t.value === v)?.label || v;

export default function JoinMovement() {
  const [form, setForm] = useState({ full_name: "", email: "", country: "", role: "youth_delegate" });
  const [loading, setLoading] = useState(false);
  const [issued, setIssued] = useState(null);
  const [supporters, setSupporters] = useState(null);

  const loadStats = () =>
    axios.get(`${API}/movement/stats`).then((r) => setSupporters(r.data.supporters)).catch(() => {});

  useEffect(() => {
    loadStats();
  }, []);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.country) {
      toast.error("Please complete every field of the charter.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/movement/register`, form);
      setIssued(data);
      toast.success("Welcome to the Union. Your Movement ID is issued.");
      loadStats();
    } catch (err) {
      if (err.response?.status === 409) {
        toast.info("This email has already joined the movement.");
      } else {
        toast.error("The registry is unreachable. Try again shortly.");
      }
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "w-full rounded-lg bg-[#0D1013] border border-[#00A651]/20 px-4 py-3 text-sm text-[#F7F5F0] placeholder:text-[#8A99AD]/60 focus:outline-none focus:border-[#00A651]/60 transition-colors";

  return (
    <section id="join" className="py-20 lg:py-32 relative noise-overlay" data-testid="join-section">
      <div className="absolute inset-0 gold-glow opacity-50" />
      <div className="relative px-4 sm:px-8 lg:px-16 max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-start">
        <div className="lg:sticky lg:top-28">
          <SectionHeader
            kicker="Ratify With Your Name"
            title="Join The Movement"
            copy="This is a call to those who want Africa to move forward. Register your support, choose your station in the Union, and receive your official Movement ID."
          />
          <div className="mt-10 flex items-center gap-8 border-y border-[#00A651]/15 py-6 max-w-md" data-testid="join-supporter-count">
            <div>
              <p className="font-display font-extrabold text-3xl text-[#00A651]">
                {supporters === null ? "—" : supporters.toLocaleString()}
              </p>
              <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD] mt-1.5">
                Registered Supporters
              </p>
            </div>
            <div>
              <p className="font-display font-extrabold text-3xl text-[#00A651]">55</p>
              <p className="font-official text-[10px] tracking-[0.2em] uppercase text-[#8A99AD] mt-1.5">
                Member States
              </p>
            </div>
          </div>
        </div>

        <div>
          <AnimatePresence mode="wait">
            {issued ? (
              <motion.div
                key="id-card"
                initial={{ opacity: 0, scale: 0.94, y: 24 }}
                animate={{ opacity: 1, scale: 1, y: 0 }}
                transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                className="rounded-2xl border border-[#00A651]/40 bg-gradient-to-br from-[#1A1F26] to-[#0A0C0E] p-8 sm:p-10 shadow-[0_32px_64px_rgba(0,0,0,0.5)]"
                data-testid="movement-id-card"
              >
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="font-official text-[10px] tracking-[0.35em] uppercase text-[#00A651]">
                      Movement Identification
                    </p>
                    <h3 className="font-display font-extrabold text-xl sm:text-2xl text-[#F7F5F0] mt-3 uppercase">
                      {issued.full_name}
                    </h3>
                  </div>
                  <Crest className="w-14 h-14 shrink-0" />
                </div>
                <div className="mt-8 space-y-3 font-official text-xs tracking-[0.15em] uppercase text-[#8A99AD]">
                  <p>ID No <span className="text-[#00A651] text-sm">{issued.movement_id}</span></p>
                  <p>Station <span className="text-[#F7F5F0]">{tierLabel(issued.role)}</span></p>
                  <p>Nation <span className="text-[#F7F5F0]">{issued.country}</span></p>
                  <p>Issued <span className="text-[#F7F5F0]">{new Date(issued.created_at).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })}</span></p>
                </div>
                <div className="mt-8 h-1.5 rounded-full overflow-hidden flex">
                  <span className="flex-1 bg-[#009B4D]" />
                  <span className="flex-1 bg-[#FFD700]" />
                  <span className="flex-1 bg-[#CE1126]" />
                </div>
                <p className="mt-6 font-serif-display italic text-[#00A651]/80 text-lg">Africa Without Border</p>
                <div className="mt-7 flex flex-wrap gap-3">
                  <a
                    href={`${API}/badges/${issued.movement_id}.png`}
                    download={`union-badge-${issued.movement_id}.png`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="download-badge-btn"
                    className="inline-flex items-center gap-2.5 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-xs px-6 py-3 hover:bg-[#2FC36A] transition-all duration-300"
                  >
                    <Download className="w-3.5 h-3.5" /> Download Union Badge
                  </a>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(`${API}/badges/${issued.movement_id}.png`);
                      toast.success("Badge link copied — rally the Union.");
                    }}
                    data-testid="copy-badge-link-btn"
                    className="inline-flex items-center gap-2.5 rounded-full border border-[#00A651]/40 text-[#00A651] font-display font-bold text-xs px-6 py-3 hover:bg-[#00A651]/10 transition-all duration-300"
                  >
                    <Link2 className="w-3.5 h-3.5" /> Copy Badge Link
                  </button>
                </div>
                <div className="mt-3 flex flex-wrap gap-3">
                  <a
                    href={`https://wa.me/?text=${encodeURIComponent(shareText(issued))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-whatsapp-btn"
                    className="inline-flex items-center gap-2.5 rounded-full border border-[#25D366]/50 text-[#25D366] font-display font-bold text-xs px-6 py-3 hover:bg-[#25D366]/10 transition-all duration-300"
                  >
                    <MessageCircle className="w-3.5 h-3.5" /> Share on WhatsApp
                  </a>
                  <a
                    href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText(issued))}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="share-x-btn"
                    className="inline-flex items-center gap-2.5 rounded-full border border-[#F7F5F0]/30 text-[#F7F5F0] font-display font-bold text-xs px-6 py-3 hover:bg-white/10 transition-all duration-300"
                  >
                    <svg viewBox="0 0 24 24" className="w-3.5 h-3.5 fill-current" aria-hidden="true">
                      <path d="M18.9 2H22l-7.03 8.03L23 22h-6.6l-5.17-6.76L5.3 22H2.2l7.52-8.59L1 2h6.77l4.67 6.17L18.9 2zm-1.16 18h1.83L6.9 3.9H4.94L17.74 20z" />
                    </svg>
                    Share on X
                  </a>
                </div>
                <button
                  onClick={() => { setIssued(null); setForm({ full_name: "", email: "", country: "", role: "youth_delegate" }); }}
                  data-testid="register-another-button"
                  className="mt-8 rounded-full border border-[#00A651]/40 text-[#00A651] font-display font-bold text-xs px-6 py-3 hover:bg-[#00A651]/10 transition-all duration-300"
                >
                  Register Another Supporter
                </button>
              </motion.div>
            ) : (
              <motion.form
                key="form"
                onSubmit={submit}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.7 }}
                className="rounded-2xl border border-[#00A651]/20 bg-[#121519] p-8 sm:p-10 space-y-5"
                data-testid="movement-signup-form"
              >
                <div>
                  <label className="font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD] block mb-2">Full Name</label>
                  <input value={form.full_name} onChange={set("full_name")} placeholder="Your name as it will appear on the charter" className={inputCls} data-testid="movement-name-input" />
                </div>
                <div>
                  <label className="font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD] block mb-2">Email</label>
                  <input type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" className={inputCls} data-testid="movement-email-input" />
                </div>
                <div className="grid sm:grid-cols-2 gap-5">
                  <div>
                    <label className="font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD] block mb-2">Nation / Diaspora</label>
                    <select value={form.country} onChange={set("country")} className={inputCls} data-testid="movement-country-select">
                      <option value="">Select nation</option>
                      {NATIONS.map((n) => (
                        <option key={n.code} value={n.name}>{n.name}</option>
                      ))}
                      <option value="Diaspora — Outside Africa">Diaspora — Outside Africa</option>
                    </select>
                  </div>
                  <div>
                    <label className="font-official text-[10px] tracking-[0.25em] uppercase text-[#8A99AD] block mb-2">Station in the Union</label>
                    <select value={form.role} onChange={set("role")} className={inputCls} data-testid="movement-role-select">
                      {TIERS.map((t) => (
                        <option key={t.value} value={t.value}>{t.label}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="movement-submit-btn"
                  className="w-full inline-flex items-center justify-center gap-3 rounded-full bg-[#00A651] text-[#0A0C0E] font-display font-bold text-sm px-7 py-4 hover:bg-[#2FC36A] hover:shadow-[0_0_32px_rgba(0,166,81,0.35)] disabled:opacity-60 transition-all duration-300"
                >
                  {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Fingerprint className="w-4 h-4" />}
                  {loading ? "Issuing Your ID..." : "Sign The Charter"}
                </button>
                <p className="font-official text-[10px] tracking-[0.15em] text-[#8A99AD]/70 text-center uppercase">
                  One name. One continent. One destiny.
                </p>
              </motion.form>
            )}
          </AnimatePresence>
        </div>
      </div>
    </section>
  );
}
